// src/pages/ScheduleContact/CheckoutModal.tsx
import React, { useEffect, useState } from "react";
import { loadTossPayments, TossPaymentsWidgets } from "@tosspayments/tosspayments-sdk";
import styles from "./Payment.module.css"; // tossReset, tossCard 등 네가 정의한 CSS
import axios from "axios";
import { nanoid } from "nanoid";
import { getCookie } from "common/Cookie";
import { ScheduleGridData } from "pages/ScheduleContact/types";

const clientKey = "test_gck_kYG57Eba3G6AN9z6aAAz8pWDOxmA";
const customerKey = "Y58ntTp8mas9XIRJShBo5";

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: { currency: string; value: number }; // KRW or USD
  country: string | null;                      // 'KR' | 'OTHER'
  onSave: (data: ScheduleGridData[]) => void;
  data: ScheduleGridData[];
  setData: any;
  onPaymentSuccess: (data: any) => void;       // 국내 결제 성공 시 사용
}

let lastOrderNumber = 0;
const generateOrderNumber = () => {
  const ts = new Date().toISOString().replace(/[-:.TZ]/g, "").substring(2, 12);
  lastOrderNumber += 1;
  return `OPS-${ts}-${String(lastOrderNumber).padStart(3, "0")}`;
};

export default function CheckoutModal({
  isOpen,
  onClose,
  amount,
  country,
  onSave,
  data,
  setData,
  onPaymentSuccess,
}: CheckoutModalProps) {
  const [ready, setReady] = useState(false);
  const [widgets, setWidgets] = useState<TossPaymentsWidgets | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);
  const [orderId] = useState<string>(() => generateOrderNumber());
  const [paymentKey] = useState<string>(() => nanoid());

  // KR에서만 SDK 로드
  useEffect(() => {
    if (!isOpen) return;

    if (country === "KR") {
      (async () => {
        try {
          const tossPayments = await loadTossPayments(clientKey);
          const w = tossPayments.widgets({ customerKey });
          setWidgets(w);
        } catch (e) {
          console.error("[CHECKOUT][SDK_LOAD_ERR]", e);
        }
      })();
      setHasRendered(false);
      setReady(false);
    } else {
      setWidgets(null);       // USD는 부모에서 위젯 사용 안 함
      setHasRendered(false);
      setReady(true);         // 버튼 활성화
    }
  }, [isOpen, country]);

  // KR에서만 위젯 렌더
  useEffect(() => {
    if (!widgets || hasRendered || country !== "KR") return;

    (async () => {
      try {
        await widgets.setAmount(amount); // { value, currency }
        await Promise.all([
          widgets.renderPaymentMethods({ selector: "#payment-method", variantKey: "DEFAULT" }),
          widgets.renderAgreement({ selector: "#agreement", variantKey: "AGREEMENT-KR" }),
        ]);
        setHasRendered(true);
        setReady(true);
      } catch (e) {
        console.error("[CHECKOUT][RENDER_ERR]", e);
      }
    })();
  }, [widgets, hasRendered, amount, country]);

  const handlePaymentClick = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const token = getCookie("accessToken");
    const isKR = country === "KR";

    // USD는 팝업 선 오픈(차단 방지)
    let popup: Window | null = null;
    if (!isKR) {
      popup = window.open(
        "",
        "paypalPaymentPopup",
        `width=600,height=800,left=${window.screen.width / 2 - 300},top=${window.screen.height / 2 - 400}`
      );
      if (!popup) {
        alert("팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.");
        setIsLoading(false);
        return;
      }
      popup.document.write('<div style="font:14px/1.4 -apple-system,Segoe UI,Roboto,sans-serif;padding:20px">Loading PayPal…</div>');
    }

    try {
      // 1) 결제 사전 등록 (소수 둘째 자리 고정)
      await axios.post(
        "/api/confirm/register",
        {
          paymentKey,
          orderId,
          amount: Number(amount.value.toFixed(2)),
          orderName: "IOPS SPACE",
          currency: isKR ? "KRW" : "USD",
          data,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (isKR) {
        // 2) 국내: Promise 방식
        if (!widgets) throw new Error("결제 위젯 초기화 실패");
        const success = await widgets.requestPayment({ orderId, orderName: "IOPS SPACE" });

        // 3) 최종 승인
        const res = await fetch("/api/confirm/widget", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId: success.orderId,
            amount: Number(amount.value.toFixed(2)),
            paymentKey: success.paymentKey,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw { message: json.message, code: json.code };
        onPaymentSuccess(json);
      } else {
        // 2) 해외: 팝업(런처)로 이동. 부모에서는 위젯 호출 금지
        const storageKey = `paypalPaymentInfo:${orderId}`;
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            orderId,
            amount: { value: Number(amount.value.toFixed(2)), currency: "USD" },
            country: "US",
            customerKey,
            data,
          })
        );
        if (popup) popup.location.href = `/mypage/payment/launcher?key=${encodeURIComponent(storageKey)}`;
        onClose();
      }
    } catch (e: any) {
      console.error("[CHECKOUT][REQUEST_FAIL]", e);
      if (e?.code !== "USER_CANCEL") alert(`결제에 실패했습니다: ${e?.message ?? e}`);
      if (popup) popup.close();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles["modal-overlay"]}>
      <div className={styles["modal-content"]}>
        <button className={styles["close-button"]} onClick={onClose}>Close</button>
        <div className={styles["wrapper"]}>
          <div className={styles["box_section"]}>
            {/* KR일 때만 위젯 컨테이너 노출 (UI 깨짐 방지용 reset 클래스 적용) */}
            {country === "KR" && (
              <div className={styles.tossCard}>
                <div id="payment-method" className={styles.tossKR} />
                <div id="agreement" className={styles.tossKR} style={{ marginTop: 12 }} />
              </div>
            )}

            {/* USD 안내 */}
            {country !== "KR" && (
              <p style={{ marginTop: 8, fontSize: 14, opacity: 0.75 }}>PayPal 결제는 새 창에서 진행됩니다.</p>
            )}

            <button
              className={styles["button"]}
              disabled={!ready || isLoading}
              onClick={handlePaymentClick}
              aria-label="tosspayment"
            >
              {isLoading ? "Processing..." : "Payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
